import type { AiSelectedAnalysisProgressEventEntity } from '@/entities/ai-selected-analysis-progress-event.entity';
import {
    OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES,
    formatKnownMarketplaceApiErrorMessageFromDescriptor,
    isAiRequestRejectedMarketplaceErrorDescriptor,
    type MarketplaceApiErrorLocale,
} from '@/shared/errors/marketplace-api-error-message';

export type AiSelectedAnalysisProgressMessageLocale = MarketplaceApiErrorLocale;

type AiSelectedAnalysisProgressMessages = {
    retrying: string;
    retryingAttempt: string;
    failed: string;
    canceled: string;
    completedSuccess: string;
    completedPartial: string;
    completedAllFailed: string;
    completedAllFailedAiRequestRejected: string;
    finalizationRefreshFailed: string;
    offerNotAccepted: string;
};

export const AI_SELECTED_ANALYSIS_PROGRESS_MESSAGES = {
    ru: {
        retrying: 'Сервис ИИ временно не принял запрос. Повторяем анализ автоматически.',
        retryingAttempt: 'Попытка {retryAttempt} из {maxAttempts}.',
        failed: 'Не удалось получить ИИ-анализ',
        canceled: 'Массовый ИИ-анализ остановлен',
        completedSuccess: 'ИИ-анализ завершен: обработано {successCount}',
        completedPartial:
            'ИИ-анализ завершён частично: успешно {successCount}, с ошибкой {failedCount}. Повторите запуск для оставшихся отзывов.',
        completedAllFailed: 'Не удалось получить ИИ-анализ',
        completedAllFailedAiRequestRejected:
            'Не удалось выполнить ИИ-анализ: сервис ИИ временно не принял запрос. Попробуйте повторить позже.',
        finalizationRefreshFailed: 'ИИ-анализ завершен, но обновление данных не выполнено',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES.ru,
    },
    en: {
        retrying: 'The AI service could not accept the request yet. Retrying analysis automatically.',
        retryingAttempt: 'Attempt {retryAttempt} of {maxAttempts}.',
        failed: 'Could not complete AI analysis',
        canceled: 'Bulk AI analysis was stopped',
        completedSuccess: 'AI analysis finished: processed {successCount}',
        completedPartial:
            'AI analysis finished partially: {successCount} succeeded, {failedCount} failed. Run it again for the remaining reviews.',
        completedAllFailed: 'Could not complete AI analysis',
        completedAllFailedAiRequestRejected:
            'Could not complete AI analysis: the AI service could not accept the request. Try again later.',
        finalizationRefreshFailed: 'AI analysis finished, but the data refresh failed',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES.en,
    },
    uz: {
        retrying: 'AI xizmati so‘rovni hozircha qabul qilmadi. Tahlil avtomatik qayta urinilmoqda.',
        retryingAttempt: '{maxAttempts} urinishdan {retryAttempt}-urinish.',
        failed: 'AI tahlilni yakunlab bo‘lmadi',
        canceled: 'Ommaviy AI tahlil to‘xtatildi',
        completedSuccess: 'AI tahlil yakunlandi: {successCount} ta qayta ishlandi',
        completedPartial:
            'AI tahlil qisman yakunlandi: {successCount} ta muvaffaqiyatli, {failedCount} ta xato. Qolgan sharhlar uchun qayta ishga tushiring.',
        completedAllFailed: 'AI tahlilni yakunlab bo‘lmadi',
        completedAllFailedAiRequestRejected:
            'AI tahlilni bajarib bo‘lmadi: AI xizmati so‘rovni vaqtincha qabul qilmadi. Keyinroq qayta urinib ko‘ring.',
        finalizationRefreshFailed: 'AI tahlil yakunlandi, lekin ma’lumotlarni yangilab bo‘lmadi',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES.uz,
    },
    'uz-Cyrl': {
        retrying: 'AI хизмати сўровни ҳозирча қабул қилмади. Таҳлил автоматик қайта уринилмоқда.',
        retryingAttempt: '{maxAttempts} уринишдан {retryAttempt}-уриниш.',
        failed: 'AI таҳлилни якунлаб бўлмади',
        canceled: 'Оммавий AI таҳлил тўхтатилди',
        completedSuccess: 'AI таҳлил якунланди: {successCount} та қайта ишланди',
        completedPartial:
            'AI таҳлил қисман якунланди: {successCount} та муваффақиятли, {failedCount} та хато. Қолган шарҳлар учун қайта ишга туширинг.',
        completedAllFailed: 'AI таҳлилни якунлаб бўлмади',
        completedAllFailedAiRequestRejected:
            'AI таҳлилни бажариб бўлмади: AI хизмати сўровни вақтинча қабул қилмади. Кейинроқ қайта уриниб кўринг.',
        finalizationRefreshFailed: 'AI таҳлил якунланди, лекин маълумотларни янгилаб бўлмади',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES['uz-Cyrl'],
    },
} as const satisfies Record<AiSelectedAnalysisProgressMessageLocale, AiSelectedAnalysisProgressMessages>;

export const getAiSelectedAnalysisProgressMessages = (
    locale: AiSelectedAnalysisProgressMessageLocale = 'ru',
): AiSelectedAnalysisProgressMessages => AI_SELECTED_ANALYSIS_PROGRESS_MESSAGES[locale];

type AiSelectedAnalysisTerminalMessageEvent = Pick<
    AiSelectedAnalysisProgressEventEntity,
    'stage' | 'errorMessage' | 'errorCode' | 'errorReason'
>;

type AiSelectedAnalysisCompletedMessageEvent = Pick<
    AiSelectedAnalysisProgressEventEntity,
    'stage' | 'successCount' | 'failedCount' | 'errorMessage' | 'errorCode' | 'errorReason'
>;

type AiSelectedAnalysisRetryingMessageEvent = Pick<
    AiSelectedAnalysisProgressEventEntity,
    'stage' | 'retryAttempt' | 'maxAttempts'
>;

const formatAiSelectedAnalysisProgressMessage = (
    template: string,
    replacements: Readonly<Record<string, string | number>>,
): string => {
    let result = template;
    for (const [name, value] of Object.entries(replacements)) {
        result = result.replace(`{${name}}`, String(value));
    }

    return result;
};

export const resolveAiSelectedAnalysisTerminalMessage = ({
    event,
    locale = 'ru',
}: {
    event: AiSelectedAnalysisTerminalMessageEvent;
    locale?: AiSelectedAnalysisProgressMessageLocale;
}): string => {
    const knownMessage = formatKnownMarketplaceApiErrorMessageFromDescriptor(event, locale);
    if (knownMessage) {
        return knownMessage;
    }

    const eventErrorMessage = event.errorMessage?.trim();
    if (eventErrorMessage) {
        return eventErrorMessage;
    }

    return AI_SELECTED_ANALYSIS_PROGRESS_MESSAGES[locale].failed;
};

export const resolveAiSelectedAnalysisRetryingMessage = ({
    event,
    locale = 'ru',
}: {
    event: AiSelectedAnalysisRetryingMessageEvent;
    locale?: AiSelectedAnalysisProgressMessageLocale;
}): string => {
    const messages = getAiSelectedAnalysisProgressMessages(locale);
    if (!event.retryAttempt || !event.maxAttempts) {
        return messages.retrying;
    }

    return `${messages.retrying} ${formatAiSelectedAnalysisProgressMessage(messages.retryingAttempt, {
        retryAttempt: event.retryAttempt,
        maxAttempts: event.maxAttempts,
    })}`;
};

export const resolveAiSelectedAnalysisCompletedMessage = ({
    event,
    locale = 'ru',
}: {
    event: AiSelectedAnalysisCompletedMessageEvent;
    locale?: AiSelectedAnalysisProgressMessageLocale;
}): string => {
    const messages = getAiSelectedAnalysisProgressMessages(locale);

    if (event.successCount <= 0) {
        return isAiRequestRejectedMarketplaceErrorDescriptor(event)
            ? messages.completedAllFailedAiRequestRejected
            : messages.completedAllFailed;
    }

    if (event.failedCount > 0) {
        return formatAiSelectedAnalysisProgressMessage(messages.completedPartial, {
            successCount: event.successCount,
            failedCount: event.failedCount,
        });
    }

    return formatAiSelectedAnalysisProgressMessage(messages.completedSuccess, {
        successCount: event.successCount,
    });
};
